import { PitchCapture, useAudioEngine } from '@tunasong/audio-ui'
import type { PitchFrame } from '@tunasong/music-lib'
import { useState } from 'react'
import type { CaptureWindowDeps } from './capture-session.js'

export interface UsePitchCaptureResult extends Pick<CaptureWindowDeps, 'startFrames' | 'setMuted'> {
  /**
   * The microphone the last window opened, as the browser names it. Null until one has opened.
   * Kept after the window closes: it is the device in use, and the user is most likely to want
   * to read it right after a take that heard nothing.
   */
  deviceLabel: string | null
  /**
   * The rate the tracker is running at.
   *
   * Carried out of the hook because `VocalLib.segmentNotes` needs it to read a frame's
   * `onsetSampleOffset`: the worklet reports a note's onset in samples, and only the capture knows
   * what a sample is worth.
   */
  sampleRate: number
}

/**
 * Wire a live microphone and the mixer's mute into `runCaptureWindow`'s injected shape.
 *
 * `setMuted` mutes the **monitor** bus. The instrument, media and peer busses all feed it, and it
 * is the only thing connected to `context.destination` — so muting it is exactly §6's "all
 * playback muted for the capture window", and nothing narrower would be: muting only the
 * instrument bus would still let a playing song bleed into the take and be scored as singing.
 *
 * A fresh `PitchCapture` per window, released when the window ends. The alternative — one
 * long-lived capture for the whole session — would hold the microphone open, and its recording
 * indicator lit, between items, which a tool whose selling point is that nothing is recorded
 * should not do.
 */
export const usePitchCapture = (): UsePitchCaptureResult => {
  const engine = useAudioEngine()
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null)

  const startFrames = async (onFrame: (frame: PitchFrame) => void) => {
    const capture = new PitchCapture({ context: engine.mixer.context })
    const listener = (ev: CustomEvent<PitchFrame>) => onFrame(ev.detail)
    capture.addFrameListener(listener)
    await capture.start()
    setDeviceLabel(capture.deviceLabel)
    return () => {
      capture.removeFrameListener(listener)
      capture.stop()
    }
  }

  const setMuted = (muted: boolean) => {
    engine.mixer.getBus('monitor').muted = muted
  }

  return { startFrames, setMuted, deviceLabel, sampleRate: engine.mixer.context.sampleRate }
}
